// NPM
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Container } from "reactstrap";
import { format } from "date-fns";

// Calance
import styles from "Pages/Admin//StatusMessages/StatusMessages.module.css";
import ReactTable from "components/ReactTable/ReactTable";
import useFetch from "Hooks/useFetch";
import useAlertContext from "Hooks/useAlertContext";

import { IStatusMessage } from "Pages/Login/Login";
import AddModal from "Pages/Admin/StatusMessages/Components/AddModal";
import EditModal from "Pages/Admin/StatusMessages/Components/EditModal";
import DeleteModal from "Pages/Admin/StatusMessages/Components/DeleteModal";
import DOMPurify from "dompurify";
import usePageTitle from "Hooks/usePageTitle";

export type IModal = "add" | "edit" | "delete" | "";

export default function StatusMessages() {
    const [messages, setMessages] = useState<IStatusMessage[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [modal, setModal] = useState<IModal>("");
    const [selectedRow, setSelectedRow] = useState<IStatusMessage>();

    usePageTitle("System Status");
    const { get } = useFetch();
    const { newAlert } = useAlertContext();

    const ignoreGetMessages = useRef(false);

    /** Fetches system status messages. Memoized for use in useEffect and in child component */
    const getMessages = useCallback(async () => {
        setIsLoading(true);
        const response = await get("/api/admin/system_status");

        let data: IStatusMessage[] | Response;
        try {
            data = await response.json();
        } catch {
            data = response;
        }
        console.log(`System status messages fetch response: `, data);

        if (ignoreGetMessages.current) return;

        if (response.ok) {
            setMessages(data as IStatusMessage[]);
        } else {
            newAlert({ message: "Error fetching messages" });
        }
        setIsLoading(false);
    }, [get, newAlert]);

    // Fetch messages on page load
    useEffect(() => {
        ignoreGetMessages.current = false;
        getMessages();

        return () => {
            ignoreGetMessages.current = true;
        };
    }, [get, getMessages, newAlert]);

    const handleButtonClick = (modal: IModal) => {
        switch (modal) {
            case "add":
                setModal(modal);
                break;
            case "edit":
                if (selectedRow) {
                    setModal(modal);
                } else {
                    newAlert({
                        message: "Please select a row to edit",
                        color: "danger",
                    });
                }
                break;
            case "delete":
                if (selectedRow) {
                    setModal(modal);
                } else {
                    newAlert({
                        message: "Please select a row to delete",
                        color: "danger",
                    });
                }
                break;
            default:
        }
    };

    const rowDoubleClickCallback = (row: any) => {
        setSelectedRow(row);
        setModal("edit");
    };

    /** State setting callback passed to children to update modal from within parent */
    const onSetModal = (modal: IModal) => {
        setModal(modal);
    };

    /** State setting callback passed to children to update selected message from within parent */
    const onSetSelectedRow = (row: any) => {
        setSelectedRow(row);
    };

    //===== Table =====
    /** Formats cells with dates in columns definition below */
    const formatDate = () => {
        return ({ value, row }: { value: string; row: any }) => {
            const type = row.original.status_type.toLowerCase();
            return (
                <div className={`text-${type}`}>
                    {format(new Date(value), "MMM dd, HH:mm")}
                </div>
            );
        };
    };

    /** Converts text links to clickable <a> elements */
    const linkify = (text: string) => {
        const clean = DOMPurify.sanitize(text);
        const urlRegex =
            /<(((https?|ftp|mailto):)?\/\/)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)>?/g;
        return clean.replace(urlRegex, (url) => {
            return `<a href="${url}" target="_blank">${url}</a>`;
        });
    };

    const columns = useMemo(() => {
        /** Formats status message column converting text links to clickable <a> elements  */
        const statusMessage = ({ value, row }: { value: string; row: any }) => {
            return (
                <div
                    className={`text-${row.original.status_type.toLowerCase()}`}
                    dangerouslySetInnerHTML={{ __html: linkify(value) }}
                />
            );
        };

        /** Formats non date cells in columns definition below */
        const colorCellText = () => {
            return ({ value, row }: { value: string; row: any }) => {
                let formattedValue;
                switch (value) {
                    case "danger":
                        formattedValue = "Error";
                        break;
                    case "warning":
                        formattedValue = "Warning";
                        break;
                    case "info":
                        formattedValue = "Info";
                        break;
                    default:
                        formattedValue = value;
                }

                return (
                    <div
                        className={`text-${row.original.status_type.toLowerCase()}`}
                    >
                        {formattedValue}
                    </div>
                );
            };
        };
        return [
            {
                Header: "Issued At",
                accessor: "issued_at",
                Cell: formatDate(),
            },
            {
                Header: "Expires At",
                accessor: "expires_at",
                Cell: formatDate(),
            },
            {
                Header: "Status Type",
                accessor: "status_type",
                Cell: colorCellText(),
            },
            {
                Header: "Status Message",
                accessor: "status_message",
                Cell: statusMessage,
            },
            {
                Header: "Issued By",
                accessor: "issued_by",
                Cell: colorCellText(),
            },
        ];
    }, []);

    // ===== Props =====
    const tableProps = {
        columns,
        data: messages,
        isLoading,
        showPagination: true,
        onSetSelectedRow,
        rowDoubleClickCallback,
    };

    const modalProps = {
        modal,
        onSetModal,
        getMessages,
        selectedRow,
        onSetSelectedRow,
    };

    return (
        <Container fluid>
            <div className={styles.headerAndButtonsBox}>
                <h1>System Status Messages</h1>
                <span className={styles.buttonsBox}>
                    <Button
                        color="primary"
                        onClick={() => handleButtonClick("add")}
                    >
                        Add
                    </Button>
                    <Button
                        color="success"
                        onClick={() => handleButtonClick("edit")}
                    >
                        Edit
                    </Button>
                    <Button
                        color="danger"
                        onClick={() => handleButtonClick("delete")}
                    >
                        Delete
                    </Button>
                </span>
            </div>

            <ReactTable {...tableProps} />

            <AddModal {...modalProps} />
            <EditModal {...modalProps} />
            <DeleteModal {...modalProps} />
        </Container>
    );
}
